import { QueryFunctionContext, QueryKey } from '@tanstack/react-query';
import { api } from '../services/api';
import { IDataNoPagination } from '../types/shared';

export type TipoArquivo = 'NFe' | 'NFSe' | 'NFCe' | 'CTe';

export interface IImportacaoPorDia {
  data: string | null;
  qt_empresas: number;
  qt_notas: number;
}

export interface IEmpresaPorTipoArquivo {
  empresa_id: number;
  nome: string | null;
  cnpj: string | null;
  razao_social: string | null;
  total_notas: number;
  codi_esp: number | null;
}

export interface IDetalhePorTipo {
  tipo: string;
  codi_esp: number | null;
  qt_saidas: number;
  qt_servicos: number;
  qt_entradas: number;
  qt_notas_total: number;
}

export interface IEmpresaPorDia {
  empresa_id: number;
  nome: string | null;
  cnpj: string | null;
  razao_social: string | null;
  total_notas: number;
  detalhes_por_tipo: IDetalhePorTipo[];
}

export interface IResumoImportacoes {
  periodo: {
    inicio: string;
    fim: string;
  };
  total_empresas: number;
  total_empresas_ativas: number;
  dias_com_importacao: number;
  total_notas: number;
  total_saidas: number;
  total_servicos: number;
  total_entradas: number;
  por_tipo: {
    nfe: number;
    nfse: number;
    nfce: number;
    cte: number;
  };
}

export interface ITopEmpresasPorTipo {
  NFe?: IEmpresaPorTipoArquivo[];
  NFSe?: IEmpresaPorTipoArquivo[];
  NFCe?: IEmpresaPorTipoArquivo[];
  CTe?: IEmpresaPorTipoArquivo[];
}

export interface IEmpresaPaginada {
  empresa_id: number;
  external_id: string | null;
  nome: string | null;
  cnpj: string | null;
  razao_social: string | null;
  total_nfe: number;
  total_nfse: number;
  total_nfce: number;
  total_cte: number;
  total_geral: number;
}

export interface IEmpresasPaginadasResponse {
  items: IEmpresaPaginada[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export const useDashboardImportacao = () => {
  const getImportacoesPorDia = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IImportacaoPorDia[]>> => {
    const filters = (key.queryKey?.[1] as Record<string, string>) ?? {};
    const res = await api.get<IDataNoPagination<IImportacaoPorDia[]>>(
      '/api/v1/dashboard-importacao/importacoes-por-dia',
      { params: filters },
    );
    return res.data;
  };

  const getTopEmpresasPorTipo = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<ITopEmpresasPorTipo>> => {
    const filters = (key.queryKey?.[1] as Record<string, string>) ?? {};
    const res = await api.get<IDataNoPagination<ITopEmpresasPorTipo>>(
      '/api/v1/dashboard-importacao/top-empresas-por-tipo',
      { params: filters },
    );
    return res.data;
  };

  const getEmpresasPorDia = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEmpresaPorDia[]>> => {
    const filters = (key.queryKey?.[1] as Record<string, string>) ?? {};
    const res = await api.get<IDataNoPagination<IEmpresaPorDia[]>>(
      '/api/v1/dashboard-importacao/empresas-por-dia',
      { params: filters },
    );
    return res.data;
  };

  const getResumoImportacoes = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IResumoImportacoes>> => {
    const filters = (key.queryKey?.[1] as Record<string, string>) ?? {};
    const res = await api.get<IDataNoPagination<IResumoImportacoes>>(
      '/api/v1/dashboard-importacao/resumo',
      { params: filters },
    );
    return res.data;
  };

  const getEmpresasPorTipoPaginado = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEmpresasPaginadasResponse>> => {
    const filters = (key.queryKey?.[1] as Record<string, string>) ?? {};
    const res = await api.get<IDataNoPagination<IEmpresasPaginadasResponse>>(
      '/api/v1/dashboard-importacao/empresas-por-tipo-paginado',
      { params: filters },
    );
    return res.data;
  };

  // Função para infinite query por tipo de arquivo
  const getEmpresasPorTipoInfinite = async ({
    pageParam = 1,
    queryKey,
  }: {
    pageParam?: number;
    queryKey: QueryKey;
  }): Promise<IEmpresasPaginadasResponse> => {
    const filters = (queryKey?.[1] as Record<string, string>) ?? {};
    const res = await api.get<IDataNoPagination<IEmpresasPaginadasResponse>>(
      '/api/v1/dashboard-importacao/empresas-por-tipo-paginado',
      { params: { ...filters, page: pageParam, size: 20 } },
    );
    return res.data.data;
  };

  return {
    getImportacoesPorDia,
    getTopEmpresasPorTipo,
    getEmpresasPorDia,
    getResumoImportacoes,
    getEmpresasPorTipoPaginado,
    getEmpresasPorTipoInfinite,
  };
};
